import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cities } from '../data/citiesData';
import { formatILS, formatPercent } from '../utils/format';
import RiskBadge from '../components/ui/RiskBadge';
import ScoreBar from '../components/ui/ScoreBar';

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
}

const ROWS = [
  {
    key: 'averagePricePerSqm',
    label: 'Prix moyen au m²',
    format: (v) => (v !== undefined && v !== null ? formatILS(v) : '—'),
    bestFn: (vals) => Math.min(...vals.filter(v => v !== null && v !== undefined)),
    bestIsBest: false, // lower is better (cheaper)
  },
  {
    key: 'priceRange',
    label: 'Fourchette de prix',
    format: (v) => (v ? `${formatILS(v.min, true)} – ${formatILS(v.max, true)}` : '—'),
    bestFn: null,
  },
  {
    key: 'avgYield',
    label: 'Rendement brut moyen',
    format: (v) => (v !== undefined && v !== null ? formatPercent(v) : '—'),
    bestFn: (vals) => Math.max(...vals.filter(v => v !== null && v !== undefined)),
    bestIsBest: true, // higher is better
  },
  {
    key: 'airbnb.averageDailyRate',
    label: 'Tarif Airbnb moyen',
    format: (v) => (v !== undefined && v !== null ? formatILS(v) + '/nuit' : '—'),
    bestFn: (vals) => Math.max(...vals.filter(v => v !== null && v !== undefined)),
    bestIsBest: true,
  },
  {
    key: 'airbnb.averageOccupancyRate',
    label: "Taux d'occupation Airbnb",
    format: (v) => (v !== undefined && v !== null ? v + ' %' : '—'),
    bestFn: (vals) => Math.max(...vals.filter(v => v !== null && v !== undefined)),
    bestIsBest: true,
  },
  {
    key: 'attractivenessScore',
    label: "Score d'attractivité",
    format: (v, city) => (v !== undefined && v !== null ? <ScoreBar score={v} /> : '—'),
    bestFn: (vals) => Math.max(...vals.filter(v => v !== null && v !== undefined)),
    bestIsBest: true,
  },
  {
    key: 'riskLevel',
    label: 'Niveau de risque',
    format: (v) => (v ? <RiskBadge level={v} /> : '—'),
    bestFn: null,
  },
  {
    key: 'recommendedProfile',
    label: 'Profil recommandé',
    format: (v) => (v ? <span style={{ fontSize: '0.82rem' }}>{v}</span> : '—'),
    bestFn: null,
  },
];

export default function Comparator() {
  const cityOptions = cities.map(c => ({ value: c.id, label: c.name }));
  const [selected, setSelected] = useState(['', '', '']);

  const enriched = cities.map(c => {
    const avg = c.rentalYields.reduce((a, b) => a + b.grossYield, 0) / c.rentalYields.length;
    return { ...c, avgYield: avg };
  });

  const selectedCities = selected.map(id => enriched.find(c => c.id === id) || null);

  const handleSelect = (colIdx, value) => {
    const next = [...selected];
    next[colIdx] = value;
    setSelected(next);
  };

  const getCellValue = (city, row) => {
    if (!city) return null;
    return getNestedValue(city, row.key);
  };

  const isBest = (row, colIdx) => {
    if (!row.bestFn) return false;
    const vals = selectedCities.map(city => getCellValue(city, row)).filter(v => v !== null && v !== undefined);
    if (vals.length < 2) return false;
    const best = row.bestFn(vals);
    const val = getCellValue(selectedCities[colIdx], row);
    return val === best;
  };

  const hasAnyCity = selectedCities.some(c => c !== null);

  return (
    <div className="page-content">
      <div className="container" style={{ maxWidth: 1100 }}>
        <span className="gold-tag">Comparateur</span>
        <h1>Comparer des villes</h1>
        <p style={{ fontSize: '1.05rem', marginBottom: '2.5rem' }}>
          Sélectionnez jusqu'à 3 villes pour comparer leurs principaux indicateurs côte à côte.
        </p>

        {/* City selectors */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {[0, 1, 2].map(colIdx => (
            <div key={colIdx}>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.4rem' }}>
                Ville {colIdx + 1}
              </label>
              <select
                className="form-select"
                value={selected[colIdx]}
                onChange={e => handleSelect(colIdx, e.target.value)}>
                <option value="">-- Choisir --</option>
                {cityOptions.map(o => (
                  <option key={o.value} value={o.value} disabled={selected.some((s, i) => i !== colIdx && s === o.value)}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {hasAnyCity ? (
          <>
            <div className="table-wrapper">
              <table style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '28%' }}>Critère</th>
                    {selectedCities.map((city, i) => (
                      <th key={i} style={{ width: '24%', textAlign: 'center', color: city ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
                        {city ? city.name : <span style={{ fontWeight: 400, fontStyle: 'italic' }}>—</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row, rowIdx) => (
                    <tr key={rowIdx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                        {row.label}
                      </td>
                      {selectedCities.map((city, colIdx) => {
                        const val = getCellValue(city, row);
                        const best = city && isBest(row, colIdx);
                        return (
                          <td key={colIdx} style={{ padding: '0.75rem 0.5rem', textAlign: 'center', background: best ? 'rgba(34,197,94,0.06)' : 'transparent' }}>
                            {city ? (
                              <span style={{ fontWeight: best ? 700 : 400, color: best ? 'var(--color-success)' : 'inherit' }}>
                                {row.format(val, city)}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Voir la fiche row */}
                  <tr>
                    <td style={{ padding: '1rem 0.5rem' }} />
                    {selectedCities.map((city, colIdx) => (
                      <td key={colIdx} style={{ padding: '1rem 0.5rem', textAlign: 'center' }}>
                        {city ? (
                          <Link to={`/villes/${city.slug}`} className="btn btn-primary btn-sm">
                            Voir la fiche
                          </Link>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="insight-box" style={{ marginTop: '1.5rem' }}>
              Les valeurs mises en vert indiquent le meilleur score parmi les villes sélectionnées. Données indicatives 2024–2025.
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--color-text-muted)', background: 'var(--color-bg-white)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'var(--color-border)', marginBottom: '1rem' }}>—</div>
            <p style={{ margin: 0 }}>Sélectionnez au moins une ville pour afficher la comparaison.</p>
          </div>
        )}
      </div>
    </div>
  );
}
